'use client';

import { useEffect, useRef, useState } from 'react';
import { ARTICLE_ATTACHMENT_MAX_FILES } from '@/config/posts';
import { FileController } from '@/controllers/file/file';
import { PostController } from '@/controllers/post/post';
import type { TEditPostAttachments } from '@/controllers/post/post.types';
import { useInlineMediaUpload } from '@/hooks/useInlineMediaUpload/useInlineMediaUpload';
import type { InlineMediaLocalEntry } from '@/hooks/useInlineMediaUpload/useInlineMediaUpload.types';
import { useRequireAuth } from '@/hooks/useRequireAuth/useRequireAuth';
import { isAppError, requiresLogin } from '@/libs/error/error.utils';
import { getImageUploadSizeLimitToastMessage } from '@/libs/image/imageUploadSizeLimit';
import { Logger } from '@/libs/logger/logger';
import { buildArticleContent } from '@/libs/post/articleContent';
import {
  countInlineMediaUris,
  serializeArticleBody,
  type SerializeArticleBodyError,
} from '@/libs/post/articleInlineMedia';
import { buildLockTeaserContent } from '@/libs/post/lockTeaser';
import { getStorageQuotaToastMessage } from '@/libs/storage/storageQuota';
import { toast } from '@/molecules/Toaster/toast';
import { FileVariant } from '@/services/nexus/file/file.types';
import { useAuthStore } from '@/stores/auth/auth.store';
import { useLocalFilesStore } from '@/stores/localFiles/localFiles.store';
import type {
  ComposerDraft,
  ExistingAttachment,
  LatestArticle,
  UsePostEditOptions,
  UsePostOptions,
  UsePostPostOptions,
  UsePostReplyOptions,
  UsePostRepostOptions,
  UsePostReturn,
} from './usePost.types';

/** User-facing copy for each publish-blocking article serialization error. */
function serializeArticleErrorMessage(error: SerializeArticleBodyError): string {
  switch (error.code) {
    case 'HAND_TYPED_ATTACHMENT_REF':
      return 'Articles cannot link files as attachment references directly. Remove them or insert the file again.';
    case 'BLOB_URI':
      return 'Articles cannot reference blob URLs. Insert the file through the editor instead.';
    case 'TOO_MANY_INLINE_IMAGES':
      return `Too many files. Articles support up to ${ARTICLE_ATTACHMENT_MAX_FILES} attachments including the cover.`;
    case 'RAW_HTML_FILE_URI':
      return 'Uploaded files cannot be used inside raw HTML. Use the editor to insert them instead.';
    case 'REFERENCE_STYLE_FILE_URI':
      return 'Uploaded files cannot use reference-style links. Use inline image syntax instead.';
    case 'UNPROCESSABLE_IMAGE':
      return 'A file in the article could not be processed. Remove it and insert it again.';
  }
}

/** Maps a File to a local-store attachment entry backed by a fresh object URL. */
function fileToLocalAttachment(file: File): InlineMediaLocalEntry {
  const url = URL.createObjectURL(file);
  const isImage = file.type.startsWith('image');
  return { type: file.type, name: file.name, urls: { main: url, feed: isImage ? url : undefined } };
}

/**
 * Custom hook to handle post creation or edits (replies, reposts, and root posts)
 *
 * @returns Object containing content state, setContent function, tags state, setTags function, attachments state, setAttachments function, isArticle state, setIsArticle function, articleTitle state, setArticleTitle function, reply method, post method, repost method, isSubmitting state, and error state
 *
 * @example
 * ```tsx
 * const { content, setContent, tags, setTags, attachments, setAttachments, reply, post, repost, isSubmitting, error } = usePost();
 *
 * // For replies:
 * const handleSubmit = reply({ postId: 'post-123', onSuccess: () => {} });
 *
 * // For reposts:
 * const handleSubmit = repost({ originalPostId: 'post-123', onSuccess: () => {} });
 *
 * // For root posts:
 * const handleSubmit = post({ onSuccess: () => {} });
 *
 * // For edits:
 * const handleSubmit = edit({ editPostId: 'post-123', onSuccess: () => {} });
 * ```
 */
/** Copy for a body that references a file this composer session does not hold (and, on an edit, the post does not own). */
const FOREIGN_INLINE_FILES_MESSAGE =
  'Some attachments reference files from outside this article. Remove them, or insert the files again so they upload fresh.';

export function usePost({ keepInlineMedia = false, active = true }: UsePostOptions = {}): UsePostReturn {
  const [content, setContent] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<ExistingAttachment[]>([]);
  const [isArticle, setIsArticle] = useState(false);
  const [articleTitle, setArticleTitle] = useState('');
  const [lockTitle, setLockTitle] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inFlight = useRef(false);
  const { waitForAuth } = useRequireAuth(active);
  // selectCurrentUserPubky() throws an error when user is not authenticated;
  // access currentUserPubky directly to get null instead (post actions return early if null)
  const currentUserId = useAuthStore((state) => state.currentUserPubky);

  // Article inline media: staged at insert time and uploaded at publish,
  // tracked per composer session for previews and best-effort cleanup. The
  // insert-time budget counts the cover plus the unique inline files already
  // in the body; `content` lags the editor by its 500ms debounce, so
  // `serializeArticleBody` at publish remains the authoritative cap enforcement.
  const inlineMediaSession = useInlineMediaUpload({
    enabled: isArticle && active,
    keepSession: keepInlineMedia,
    authorPubky: currentUserId,
    getInlineBudget: () =>
      ARTICLE_ATTACHMENT_MAX_FILES -
      Math.min(attachments.length + existingAttachments.length, 1) -
      (currentUserId ? countInlineMediaUris(content, currentUserId) : 0),
  });

  /**
   * Maps a failed commit to its toast. A full homeserver storage quota warns and says why, since
   * retrying cannot help; the image size limit keeps its specific message; anything else falls back
   * to the generic retry copy (issue #1776).
   */
  const showCommitErrorToast = (error: unknown, fallbackDescription: string) => {
    const storageQuotaMessage = getStorageQuotaToastMessage(error);
    if (storageQuotaMessage) {
      toast({ variant: 'warning', description: storageQuotaMessage });
      return;
    }

    // A write the homeserver rejected as unauthenticated (`UNAUTHORIZED` or `SESSION_EXPIRED`)
    // cannot succeed by retrying: the session has to be re-established first, so point at
    // sign-in instead of asking for a retry that keeps failing (issue #2555; mirrors the profile
    // form's copy). Nothing here signs the user out or retries the write; the caller's draft is
    // left untouched.
    if (isAppError(error) && requiresLogin(error)) {
      toast({ variant: 'error', description: 'Session expired. Please sign in.' });
      return;
    }

    toast({
      variant: 'error',
      description: getImageUploadSizeLimitToastMessage(error) ?? fallbackDescription,
    });
  };

  /**
   * Serializes an article body for publishing: rewrites author-owned inline
   * file URIs to `attachment:{n}` slots, counted from `slotOffset` (0 for a
   * published article, whose cover is in the envelope; 1 for a locked article
   * with a cover in slot 0). Returns null (after toasting) when the body
   * contains destinations that block publishing.
   */
  const serializeArticleForPublish = (
    slotOffset: 0 | 1,
    body = content.trim(),
  ): { body: string; inlineUris: string[] } | null => {
    if (!currentUserId) return null;
    const serialized = serializeArticleBody({
      body,
      slotOffset,
      authorPubky: currentUserId,
      maxInlineMedia: ARTICLE_ATTACHMENT_MAX_FILES - slotOffset,
    });
    if (serialized.errors.length > 0) {
      toast({ variant: 'error', description: serializeArticleErrorMessage(serialized.errors[0]) });
      return null;
    }
    return { body: serialized.body, inlineUris: serialized.inlineUris };
  };

  /**
   * Blocks (with a toast) an edit whose inline files this article may not
   * manage: every attachment slot is subject to hard deletion when a later
   * edit drops it, so a file shared with another post would break that post
   * permanently. Only files staged this composer session, or the post's own
   * original attachments, may become attachment slots.
   */
  const rejectForeignInlineUris = (inlineUris: string[], originalUris: readonly string[]): boolean => {
    const originalSet = new Set(originalUris);
    const hasForeign = inlineUris.some(
      (uri) => inlineMediaSession.getSessionFile(uri) === null && !originalSet.has(uri),
    );
    if (!hasForeign) return false;

    toast({ variant: 'error', description: FOREIGN_INLINE_FILES_MESSAGE });
    return true;
  };

  /**
   * An article body in published form with the session files its inline slots
   * carry, for a create or a lock capture: every file goes up with the post, so
   * every inline URI must be a file staged this session. Null, after a toast,
   * when the body cannot be published or references another file — a file from
   * another post would break that post when a later edit drops its slot, and a
   * skipped file would shift every slot after it onto the wrong file.
   */
  const serializeArticleWithSessionFiles = (
    body: string,
    slotOffset: 0 | 1,
  ): { body: string; inlineUris: string[]; inlineFiles: File[] } | null => {
    const serialized = serializeArticleForPublish(slotOffset, body.trim());
    if (!serialized) return null;

    const inlineFiles = serialized.inlineUris.map((uri) => inlineMediaSession.getSessionFile(uri));
    if (inlineFiles.every((file) => file !== null)) return { ...serialized, inlineFiles };

    toast({ variant: 'error', description: FOREIGN_INLINE_FILES_MESSAGE });
    return null;
  };

  /**
   * Completes inline seed entries: this session's files keep their object
   * URLs; URIs kept from previous sessions fall back to locally cached
   * metadata + CDN URLs (long since generated for old files). Without this,
   * one kept file would void the whole seed — stripping fresh uploads
   * (including a brand-new cover) of their object URLs and exposing them to
   * the CDN variant-readiness window, where fresh variants 404 for a while.
   */
  const completeInlineSeedEntries = async (inlineUris: string[]): Promise<(InlineMediaLocalEntry | null)[]> => {
    const sessionEntries = inlineMediaSession.buildLocalAttachmentEntries(inlineUris);
    const missingUris = inlineUris.filter((_, index) => sessionEntries[index] === null);
    if (missingUris.length === 0) return sessionEntries;

    try {
      const metadata = await FileController.getMetadata({ fileAttachments: missingUris });
      const metadataByUri = new Map(metadata.map((file) => [file.uri, file]));
      return inlineUris.map((uri, index) => {
        const sessionEntry = sessionEntries[index];
        if (sessionEntry) return sessionEntry;
        const fileMetadata = metadataByUri.get(uri);
        if (!fileMetadata) return null;
        const isImage = fileMetadata.content_type.startsWith('image');
        return {
          type: fileMetadata.content_type,
          name: fileMetadata.name,
          urls: {
            main: FileController.getFileUrl({ fileId: fileMetadata.id, variant: FileVariant.MAIN }),
            feed: isImage
              ? FileController.getFileUrl({ fileId: fileMetadata.id, variant: FileVariant.FEED })
              : undefined,
            // A kept cover renders from here, so the desktop hero needs its derived variant too.
            large: isImage
              ? FileController.getFileUrl({ fileId: fileMetadata.id, variant: FileVariant.LARGE })
              : undefined,
          },
        };
      });
    } catch {
      // Best-effort: an incomplete seed just defers rendering to the Dexie/CDN path
      return sessionEntries;
    }
  };

  /**
   * Seeds the local files store so the creating session renders instantly (the CDN may not have
   * generated variants yet): the inline entries index-aligned with the post's attachments, and the
   * cover in its own entry (it is no slot). If any inline entry is unknown (e.g. an inline image
   * kept from a previous session), the list is cleared and the Dexie/CDN path takes over; a cover
   * without resolved URLs (`null`) likewise.
   */
  const seedArticleLocalFiles = (
    postId: string,
    entries: (InlineMediaLocalEntry | null)[],
    cover: InlineMediaLocalEntry | null | undefined,
  ) => {
    const complete = entries.every((entry) => entry !== null);
    useLocalFilesStore.getState().setPostAttachments(postId, complete ? (entries as InlineMediaLocalEntry[]) : []);
    useLocalFilesStore.getState().setArticleCover(postId, cover ?? null);
  };

  const reply = async ({ postId, onSuccess }: UsePostReplyOptions) => {
    // allow empty content and attachments
    if ((!content.trim() && attachments.length === 0) || !postId || !currentUserId) return;

    if (inFlight.current) return;
    inFlight.current = true;
    setIsSubmitting(true);

    try {
      if (!(await waitForAuth())) return;
      const createdPostId = await PostController.commitCreate({
        parentPostId: postId,
        content: content.trim(),
        authorId: currentUserId,
        tags: tags.length > 0 ? tags : undefined,
        attachments: attachments.length > 0 ? attachments : undefined,
      });
      setContent('');
      setTags([]);
      setAttachments([]);
      toast({
        title: 'Reply posted',
        dismissButton: true,
      });
      onSuccess?.(createdPostId);
    } catch (err) {
      Logger.error('[usePost] Failed to submit reply:', err);
      showCommitErrorToast(err, 'Could not post reply. Try again.');
    } finally {
      inFlight.current = false;
      setIsSubmitting(false);
    }
  };

  // The inputs run ahead of the debounced state: a publish takes what the editor holds, not what
  // the state last caught up with, or an image inserted in the last half second is dropped and its
  // staged file released as unreferenced. A plain post has no article and reads the state.
  const latestOf = (article?: LatestArticle) => ({
    title: article?.title ?? articleTitle,
    body: article?.body ?? content,
  });

  const post = async ({ article, embedUrl, onSuccess }: UsePostPostOptions) => {
    const { title: latestTitle, body: latestBody } = latestOf(article);
    const hasBody = Boolean(latestBody.trim());
    // allow empty content and attachments if not article
    if ((!hasBody && attachments.length === 0) || (isArticle && (!hasBody || !latestTitle.trim())) || !currentUserId)
      return;

    if (inFlight.current) return;
    inFlight.current = true;
    setIsSubmitting(true);
    try {
      if (!(await waitForAuth())) return;
      // Only protect files once the commit can start; closing during restore may discard them.
      inlineMediaSession.setCommitting(true);
      // A published article's cover lives in the envelope, so its inline slots count from 0.
      const article = isArticle ? serializeArticleWithSessionFiles(latestBody, 0) : null;
      if (isArticle && !article) return;

      // An article's inline files go up with the cover, in slot order (the order the serialized
      // body references), through the controller: a failed publish rolls every upload back, so
      // nothing of an unpublished article ever stays on the homeserver.
      const coverFile: File | undefined = article ? attachments[0] : undefined;
      const uploads = article ? article.inlineFiles : attachments;
      const createdPostId = await PostController.commitCreate({
        content: article ? buildArticleContent(latestTitle, article.body) : content.trim(),
        authorId: currentUserId,
        tags: tags.length > 0 ? tags : undefined,
        attachments: uploads.length > 0 ? uploads : undefined,
        coverImage: coverFile,
        isArticle,
        embedUrl: article ? undefined : embedUrl,
      });

      if (article) {
        // Build entries before finalizeSession clears the session map; the
        // referenced object URLs' ownership moves to the store.
        seedArticleLocalFiles(
          createdPostId,
          await completeInlineSeedEntries(article.inlineUris),
          coverFile ? fileToLocalAttachment(coverFile) : undefined,
        );
        void inlineMediaSession.finalizeSession(article.inlineUris);
      }

      setContent('');
      setTags([]);
      setAttachments([]);
      setIsArticle(false);
      setArticleTitle('');
      toast({
        title: 'Post published',
      });
      onSuccess?.(createdPostId);
    } catch (err) {
      Logger.error('[usePost] Failed to create post:', err);
      showCommitErrorToast(err, 'Could not create post. Try again.');
    } finally {
      inlineMediaSession.setCommitting(false);
      inFlight.current = false;
      setIsSubmitting(false);
    }
  };

  const repost = async ({ originalPostId, successToastTitle, onSuccess, onUndo }: UsePostRepostOptions) => {
    if (!originalPostId || !currentUserId) return;

    if (inFlight.current) return;
    inFlight.current = true;
    setIsSubmitting(true);

    try {
      if (!(await waitForAuth())) return;
      const createdPostId = await PostController.commitCreate({
        originalPostId,
        content: content.trim(),
        authorId: currentUserId,
        tags: tags.length > 0 ? tags : undefined,
        attachments: attachments.length > 0 ? attachments : undefined,
      });
      setContent('');
      setTags([]);
      setAttachments([]);

      toast({
        title: successToastTitle ?? 'Reposted',
        action: { label: 'Undo', altText: 'Undo', onClick: () => onUndo(createdPostId) },
      });

      onSuccess?.(createdPostId);
    } catch (err) {
      Logger.error('[usePost] Failed to repost:', err);
      showCommitErrorToast(err, 'Could not repost. Try again.');
    } finally {
      inFlight.current = false;
      setIsSubmitting(false);
    }
  };

  const edit = async ({
    editPostId,
    isLockAnnouncement,
    lockContentKind,
    originalAttachmentUris,
    originalCoverUri,
    preservedAttachmentUris,
    article,
    onSuccess,
  }: UsePostEditOptions) => {
    const { title: latestTitle, body: latestBody } = latestOf(article);
    const hasBody = Boolean(latestBody.trim());
    // allow empty content when attachments remain; articles require content and title
    const totalAttachments = existingAttachments.length + attachments.length;
    if (
      (!hasBody && totalAttachments === 0) ||
      (isArticle && (!hasBody || !latestTitle.trim())) ||
      !editPostId ||
      !currentUserId
    )
      return;

    if (inFlight.current) return;
    inFlight.current = true;
    setIsSubmitting(true);
    try {
      if (!(await waitForAuth())) return;
      // Only protect files once the commit can start; closing during restore may discard them.
      inlineMediaSession.setCommitting(true);
      let editContentPayload: string;
      let editAttachments: TEditPostAttachments | undefined;
      let editCoverImage: string | null | undefined;
      let articleSeedEntries: (InlineMediaLocalEntry | null)[] | undefined;
      let articleCoverEntry: InlineMediaLocalEntry | null | undefined;
      let articleReferencedUris: string[] | undefined;

      if (isArticle) {
        // Article edits use the slot-ordered `nextOrder` contract: inline body media in
        // first-appearance order, then the originals the user was never shown. The cover (kept or
        // freshly uploaded) is no slot: it goes in the envelope through `coverImage`, whatever
        // shape the stored version had (ADR-0023 as amended by ADR-0027).
        const keptCover = existingAttachments[0];
        const newCoverFile: File | undefined = attachments[0];

        // Validate the body before uploading a replacement cover, so a blocked
        // publish uploads nothing.
        const serialized = serializeArticleForPublish(0, latestBody.trim());
        if (!serialized) return;

        // Removal is diffed against the seeded snapshot, never the live row.
        const originalUris = originalAttachmentUris ?? existingAttachments.map((attachment) => attachment.uri);
        const originalSet = new Set(originalUris);
        // The stored cover may be referenced inline too (one file, one URI): it is the post's own.
        if (
          rejectForeignInlineUris(serialized.inlineUris, [
            ...originalUris,
            ...(originalCoverUri ? [originalCoverUri] : []),
          ])
        )
          return;

        // Original attachments the user was never shown (not the cover, not
        // referenced by the body at open) ride at the tail so an unrelated
        // edit never deletes files the user did not see and remove. Slots of
        // referenced files are unaffected by the tail.
        const keptCoverUri = newCoverFile ? undefined : keptCover?.uri;
        const referencedSet = new Set(serialized.inlineUris);
        const preservedTail = (preservedAttachmentUris ?? []).filter(
          (uri) => originalSet.has(uri) && !referencedSet.has(uri) && uri !== keptCoverUri,
        );

        // The serialize cap covers the inline media only; the preserved tail
        // can push the total past the spec limit, which would otherwise
        // surface as an opaque spec error from `editPost`. Checked
        // before anything goes up, so a blocked save uploads nothing.
        if (serialized.inlineUris.length + preservedTail.length > ARTICLE_ATTACHMENT_MAX_FILES) {
          toast({
            variant: 'error',
            description: `Articles support up to ${ARTICLE_ATTACHMENT_MAX_FILES} attachments, including attachments kept from previous versions. Remove some images to save.`,
          });
          return;
        }

        // Nothing is uploaded before this point. The replacement cover and the files staged this
        // session go up now, under their homeserver URIs: the slot-ordered `nextOrder` contract
        // takes URIs only. They stay in the session if the commit fails — reused by a retry,
        // deleted by the discard sweep.
        let coverUri = keptCoverUri;
        if (newCoverFile) {
          coverUri = await FileController.commitCreate({ file: newCoverFile, pubky: currentUserId });
          inlineMediaSession.registerSessionUpload(coverUri, newCoverFile);
        }
        const inlineUris = await inlineMediaSession.uploadStaged(serialized.inlineUris);
        const nextOrder = [...inlineUris, ...preservedTail];
        const kept = [...new Set(nextOrder.filter((uri) => originalSet.has(uri)))];
        const addedUris = [...new Set(nextOrder.filter((uri) => !originalSet.has(uri)))];
        const orderChanged =
          nextOrder.length !== originalUris.length || nextOrder.some((uri, index) => uri !== originalUris[index]);
        const coverChanged = (coverUri ?? null) !== (originalCoverUri ?? null);

        editContentPayload = buildArticleContent(latestTitle, serialized.body);
        // A stored version whose cover still sat in slot 0 (a 0.x article) reads as changed: its
        // attachments lose that slot and the cover moves into the envelope.
        editAttachments =
          orderChanged || coverChanged ? { original: originalUris, kept, added: [], addedUris, nextOrder } : undefined;
        editCoverImage = orderChanged || coverChanged ? (coverUri ?? null) : undefined;
        articleReferencedUris = [...nextOrder, ...(coverUri ? [coverUri] : [])];

        // undefined = no cover; null = cover exists but has no resolved URLs
        articleCoverEntry = newCoverFile
          ? fileToLocalAttachment(newCoverFile)
          : keptCover
            ? keptCover.urls
              ? { type: keptCover.type, name: keptCover.name, urls: keptCover.urls }
              : null
            : undefined;
        // Seed entries must be index-aligned with the FULL nextOrder,
        // including the preserved tail (metadata completion covers it)
        articleSeedEntries = await completeInlineSeedEntries(nextOrder);
      } else {
        // Removal is the only mutation of kept attachments (no reordering), so a
        // count comparison against the seeded snapshot detects any change.
        const keptUris = existingAttachments.map((attachment) => attachment.uri);
        const originalUris = originalAttachmentUris ?? keptUris;
        const attachmentsChanged = attachments.length > 0 || keptUris.length !== originalUris.length;
        editContentPayload = isLockAnnouncement
          ? buildLockTeaserContent({
              lock_title: lockTitle,
              teaser_description: content,
              content_kind: lockContentKind,
            })
          : content.trim();
        editAttachments = attachmentsChanged
          ? { original: originalUris, kept: keptUris, added: attachments }
          : undefined;
      }

      await PostController.commitEdit({
        compositePostId: editPostId,
        content: editContentPayload,
        attachments: editAttachments,
        coverImage: editCoverImage,
      });

      if (isArticle && articleSeedEntries && articleReferencedUris) {
        seedArticleLocalFiles(editPostId, articleSeedEntries, articleCoverEntry);
        void inlineMediaSession.finalizeSession(articleReferencedUris);
      }

      setContent('');
      setAttachments([]);
      setExistingAttachments([]);
      setIsArticle(false);
      setArticleTitle('');
      setLockTitle('');
      toast({
        title: 'Post updated',
      });
      onSuccess?.(editPostId);
    } catch (err) {
      Logger.error('[usePost] Failed to edit post:', err);
      showCommitErrorToast(err, 'Could not update post. Try again.');
    } finally {
      inlineMediaSession.setCommitting(false);
      inFlight.current = false;
      setIsSubmitting(false);
    }
  };

  // Set when a composer draft is restored whole (an abandoned lock): the restored article mode and
  // its cover land in the same commit, which the clearing effect below must not read as a switch.
  const restoringDraftRef = useRef(false);

  /**
   * Puts a captured composer draft back as it was. Setting the fields one by one would trip the
   * article-mode effect below: it sees `isArticle` flip true next to a non-empty `attachments` and
   * clears the cover with the "one cover image" warning (the lock-abandon regression).
   */
  const restoreComposerDraft = (draft: ComposerDraft) => {
    // Armed only when the restore flips the mode: a draft put back over an uncleared composer runs
    // no effect, and a latched flag would skip the next real switch into article mode
    restoringDraftRef.current = draft.isArticle && !isArticle && draft.attachments.length > 0;
    setContent(draft.content);
    setAttachments(draft.attachments);
    setIsArticle(draft.isArticle);
    setArticleTitle(draft.articleTitle);
  };

  // Clear attachments when switching to article mode.
  // `existingAttachments` is deliberately excluded: it is only populated in
  // edit mode, where `isArticle` flips true during prefill in the same commit
  // window that seeds the article cover — including it here would wipe the
  // seeded cover on mount. The article button is hidden in edit mode, so a
  // user-initiated switch can never happen with `existingAttachments` set.
  useEffect(() => {
    if (restoringDraftRef.current) {
      // A restored draft brings its cover back on purpose
      restoringDraftRef.current = false;
      return;
    }
    if (isArticle && attachments.length > 0) {
      toast({
        variant: 'warning',
        title: 'Articles support one cover image',
      });
      setAttachments([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only trigger on isArticle change, not attachments
  }, [isArticle]);

  return {
    content,
    setContent,
    tags,
    setTags,
    attachments,
    setAttachments,
    existingAttachments,
    setExistingAttachments,
    isArticle,
    setIsArticle,
    articleTitle,
    setArticleTitle,
    lockTitle,
    setLockTitle,
    restoreComposerDraft,
    reply,
    post,
    repost,
    edit,
    isSubmitting,
    inlineMedia: {
      upload: inlineMediaSession.stageInlineMedia,
      getPreviewUrl: inlineMediaSession.getPreviewUrl,
      getMediaType: inlineMediaSession.getMediaType,
      getMediaName: inlineMediaSession.getMediaName,
    },
    // A locked article keeps its cover in slot 0 inside the lock, so its inline slots count from 1.
    serializeArticleForLock: (body) => serializeArticleWithSessionFiles(body, attachments.length > 0 ? 1 : 0),
  };
}
